import React from "react";
import { Box, Fab } from "@mui/material";
import { BugReport } from "@mui/icons-material";
import HeroSection from "./components/main/HeroSection";
import FloatingTrustBadge from "./components/main/FloatingTrustBadge";
import type { HeroSlide } from "./components/main/HeroSection";

const B2C_SLIDES: HeroSlide[] = [
    { id: 1, imageSrc: '/assets/hero_b2c_pc1.jpg', imageSrcMobile: '/assets/hero_b2c_m1.jpg', imageAlt: '', buttonAction: 'quote' },
    { id: 2, imageSrc: '/assets/hero_b2c_pc2.jpg', imageSrcMobile: '/assets/hero_b2c_m2.jpg', imageAlt: '', buttonAction: 'quote' },
    { id: 3, imageSrc: '/assets/hero_b2c_pc3.jpg', imageSrcMobile: '/assets/hero_b2c_m3.jpg', imageAlt: '', buttonAction: 'quote' },
    { id: 4, imageSrc: '/assets/hero_b2c_pc4.jpg', imageSrcMobile: '/assets/hero_b2c_m4.jpg', imageAlt: '', buttonAction: 'quote' },
    { id: 5, imageSrc: '/assets/hero_b2c_pc5.jpg', imageSrcMobile: '/assets/hero_b2c_m5.jpg', imageAlt: '', buttonAction: 'scroll-steps' },
    { id: 6, imageSrc: '/assets/hero_b2c_pc6.jpg', imageSrcMobile: '/assets/hero_b2c_m6.jpg', imageAlt: '', buttonAction: 'navigate', buttonTarget: '/trust' },
    { id: 7, imageSrc: '/assets/hero_b2c_pc7.jpg', imageSrcMobile: '/assets/hero_b2c_m7.jpg', imageAlt: '', buttonAction: 'scroll-steps' },
];
import AppBarSection from "./components/main/AppBarSection";
import StepsSection from "./components/main/StepsSection";
import CtaSection from "./components/main/CtaSection";
import DevicePurchaseSection from "./components/main/DevicePurchaseSection";
import BlanccoSection from "./components/main/BlanccoSection";
import ReviewSection from "./components/main/ReviewSection";
import TodaysPriceSection from "./components/main/TodaysPriceSection";
import Footer from "./components/main/Footer";
import UrgentBanner from "./components/notice/UrgentBanner";
import NoticeSection from "./components/notice/NoticeSection";
import NoticeTicker from "./components/notice/NoticeTicker";
import PromoPopup from "./components/PromoPopup";
import { logError } from '../utils/sentry';
import { trackBusinessMetrics } from '../utils/sentryMetrics';
import { handleError } from '../utils/errorNotification';
import { useChannel } from '../contexts/ChannelContext';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// B2C 개인고객 전용 섹션 컴포넌트
import TrustSection from "./components/main/personal/TrustSection";
import PersonalStepsSection from "./components/main/personal/PersonalStepsSection";
import PersonalCtaSection from "./components/main/personal/PersonalCtaSection";

const MainPage = () => {
    const { isPersonal } = useChannel();

    // 테스트용 에러 트리거 함수들
    const triggerError = () => {
        console.log('🔴 JavaScript 에러 테스트 버튼 클릭됨');

        try {
            const testError = new Error('Sentry 테스트용 JavaScript 에러입니다!');
            logError(testError, {
                test_type: 'manual_trigger',
                button_clicked: 'javascript_error_test',
                timestamp: new Date().toISOString()
            });

            console.log('📤 logError 함수 호출 완료');

            setTimeout(() => {
                throw testError;
            }, 100);

        } catch (error) {
            console.error('❌ 에러 트리거 중 문제 발생:', error);
        }
    };

    const triggerBusinessError = () => {
        console.log('🟡 비즈니스 에러 테스트 버튼 클릭됨');

        handleError(new Error('비즈니스 로직 테스트 에러'), {
            business_type: 'application_submit',
            test_context: 'sentry_integration_test',
            user_action: 'test_button_click',
            timestamp: new Date().toISOString()
        }, 'critical');

        console.log('📤 handleError 함수 호출 완료');
    };

    const triggerMetrics = () => {
        console.log('🟢 메트릭 테스트 버튼 클릭됨');

        trackBusinessMetrics.applicationSubmitSuccess(2, '테스트사용자');
        trackBusinessMetrics.authenticationSuccess('portone');

        import('../utils/sentry').then(({ trackBusinessSuccess }) => {
            trackBusinessSuccess('application_submit', '메트릭 테스트 성공', {
                test_type: 'manual_metrics_test'
            });
        });

        console.log('📊 메트릭 전송 완료');
        alert('Sentry 메트릭이 전송되었습니다! 콘솔을 확인하세요.');
    };

    return (
        <Box
            sx={{
                background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
                minHeight: "100vh"
            }}
        >
            <FloatingTrustBadge />
            <PromoPopup />
            <AppBarSection />
            <NoticeTicker />
            <UrgentBanner />

            {/* B2B / B2C 공통 상단 영역 (한 화면에 표시) */}
            <HeroSection slides={isPersonal ? B2C_SLIDES : undefined} />
            <DevicePurchaseSection />
            <TodaysPriceSection />
            <BlanccoSection />

            {isPersonal ? (
                <>
                    {/* B2C 개인고객 전용 섹션 */}
                    <TrustSection />
                    <PersonalStepsSection />
                    <PersonalCtaSection />
                </>
            ) : (
                <>
                    {/* B2B 거래처 전용 섹션 */}
                    <StepsSection />
                    <CtaSection />
                    <NoticeSection />
                </>
            )}

            {/* 리뷰 섹션 - 맨 아래 */}
            <ReviewSection />

            <Footer />

            {/* 개발 환경에서만 보이는 Sentry 테스트 버튼들 */}
            {import.meta.env.DEV && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 90, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Fab
                        color="error"
                        size="small"
                        onClick={triggerError}
                        title="JavaScript 에러 테스트"
                    >
                        <BugReport />
                    </Fab>
                    <Fab
                        color="warning"
                        size="small"
                        onClick={triggerBusinessError}
                        title="비즈니스 에러 테스트"
                        sx={{ bgcolor: '#ff9800' }}
                    >
                        ⚠️
                    </Fab>
                    <Fab
                        color="success"
                        size="small"
                        onClick={triggerMetrics}
                        title="성공 메트릭 테스트"
                    >
                        📊
                    </Fab>
                </Box>
            )}
        </Box>
    );
};

export default MainPage;
